// src/pages/Accounting/BankTransactions/components/BankLogo.tsx

import React from "react";
import { BANK_BRAND_COLORS } from "../constants";

type Size = "xs" | "sm" | "md";

interface BankLogoProps {
  code: string;
  shortName: string;
  size?: Size;
}

const SIZES: Record<
  Size,
  { d: number; font: number; dotSize: number; dotOffset: number }
> = {
  xs: { d: 24, font: 9, dotSize: 6, dotOffset: 2 },
  sm: { d: 32, font: 11, dotSize: 7, dotOffset: 2 },
  md: { d: 40, font: 13, dotSize: 8, dotOffset: 3 },
};

/**
 * Picks a readable text color based on background luminance.
 * Light backgrounds (like CBA yellow or BB yellow) get dark text; everything
 * else gets white. Formula is perceived luminance (ITU BT.601).
 */
function readableTextColor(hex: string): string {
  const clean = hex.replace("#", "");
  if (clean.length < 6) return "#fff";
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 160 ? "#0E1A1A" : "#fff";
}

export function BankLogo({ code, shortName, size = "sm" }: BankLogoProps) {
  const brand = BANK_BRAND_COLORS[code] ?? { bg: "#607D8B" };
  const spec = SIZES[size];
  const fontSize = brand.textSize ?? spec.font;
  const textColor = readableTextColor(brand.bg);

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        width: spec.d,
        height: spec.d,
        borderRadius: 8,
        background: brand.bg,
        color: textColor,
        fontSize,
        fontWeight: 800,
        letterSpacing: "-0.01em",
        fontFamily: "Inter, -apple-system, sans-serif",
        flexShrink: 0,
      }}
    >
      {shortName}
      {brand.dot && (
        <span
          style={{
            position: "absolute",
            top: spec.dotOffset,
            right: spec.dotOffset,
            width: spec.dotSize,
            height: spec.dotSize,
            borderRadius: "50%",
            background: brand.dot,
          }}
        />
      )}
    </span>
  );
}